import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAutoPaginationNavigation } from '@/utils/autoPaginationNavigation';
import { getErrorMessage } from '@/utils/errorUtils';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  Typography,
  Link,
  Divider,
  IconButton,
  Alert,
  Tooltip,
  Button,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  GetApp as DownloadIcon,
  Visibility as PreviewIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import Images from '@/assets/Images';
import styles from '../AssetManagement/AssetDetails/AssetDetails.module.scss';
import activityService, { ActivityDetail, ActivityMovementSession } from '../../services/activityService';
import { downloadDocument, handleDownload, handlePreview } from '../../services/documentService';
import { useToast } from '../../components/common/Toast/ToastProvider';
import { PermissionButton } from '../../components/common/PermissionButton';
import { formatDate as formatDateUtil } from '@/utils/formatters';

const ActivityDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { backToListing } = useAutoPaginationNavigation(navigate, '/schedule-management/activities');

  // State management
  const [activity, setActivity] = useState<ActivityDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [movementSessions, setMovementSessions] = useState<ActivityMovementSession[]>([]);
  const [loadingMovementSessions, setLoadingMovementSessions] = useState(false);
  const [movementSessionsError, setMovementSessionsError] = useState<string | null>(null);
  const [forceAction, setForceAction] = useState<'checkout' | 'checkin' | null>(null);
  const [forceComments, setForceComments] = useState('');
  const [forceSubmitting, setForceSubmitting] = useState(false);
  const { showToast } = useToast();

  // Fetch activity details
  useEffect(() => {
    const fetchActivityDetails = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const activityData = await activityService.getActivityDetails(id);
        setActivity(activityData);
        
      } catch (err) {
        console.error('Error fetching activity details:', err);
        setError('Failed to load activity details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchActivityDetails();
  }, [id]);

  const fetchMovementSessions = async () => {
    if (!id) return;
    try {
      setLoadingMovementSessions(true);
      setMovementSessionsError(null);
      const sessions = await activityService.getActivityMovementSessions(id);
      setMovementSessions(sessions || []);
    } catch (err) {
      console.error('Error fetching activity movement sessions:', err);
      setMovementSessionsError('Failed to load movement sessions. Please try again later.');
    } finally {
      setLoadingMovementSessions(false);
    }
  };

  // Fetch the RFID movement session history - only relevant for
  // movement-tracked activities.
  useEffect(() => {
    fetchMovementSessions();
  }, [id]);

  const hasAuthorizedMovement = (statuses: string[]) =>
    movementSessions.some((session) => session.is_authorized && statuses.includes(session.movement_status || ''));
  const hasCheckout = hasAuthorizedMovement(['checkout', 'force_checkout']);
  const hasCheckin = hasAuthorizedMovement(['checkin', 'force_checkin']);

  const handleOpenForceDialog = (action: 'checkout' | 'checkin') => {
    setForceAction(action);
    setForceComments('');
  };

  const handleCloseForceDialog = () => {
    setForceAction(null);
    setForceComments('');
  };

  const handleForceSubmit = async () => {
    if (!forceAction || !forceComments.trim() || !id) return;

    try {
      setForceSubmitting(true);
      const updated = forceAction === 'checkout'
        ? await activityService.forceCheckoutActivity(id, forceComments.trim())
        : await activityService.forceCheckinActivity(id, forceComments.trim());
      setActivity(updated);
      await fetchMovementSessions();
      showToast(
        forceAction === 'checkout'
          ? 'Activity force-checked-out successfully!'
          : 'Activity force-checked-in successfully!',
        'success'
      );
      handleCloseForceDialog();
    } catch (err) {
      const message = getErrorMessage(err, `Failed to force ${forceAction === 'checkout' ? 'check-out' : 'check-in'}.`);
      showToast(message, 'error');
      console.error('Error force-closing activity movement step:', err);
    } finally {
      setForceSubmitting(false);
    }
  };

  const formatReaderLocation = (session: ActivityMovementSession) => {
    if (!session.reader_detail) return 'Force-closed (no RFID scan)';
    const locationName = session.reader_detail.location_detail?.name;
    return locationName
      ? `${session.reader_detail.name} (${locationName})`
      : session.reader_detail.name;
  };

  const handleFileDownload = async (file: any) => {
    try {
      if (!file?.id) {
        console.error('No file ID available for download:', file);
        showToast('File ID is not available for download.', 'error');
        return;
      }
      
      await downloadDocument(file.id);
    } catch (err) {
      console.error('Error downloading file:', err);
      showToast('Failed to download file. Please try again.', 'error');
    }
  };

  const handlePermitDownload = async () => {
    const documentId = activity?.permit_detail?.document;
    if (!documentId) return;
    
    try {
      await downloadDocument(documentId);
      showToast('Activity Permit downloaded successfully!', 'success');
    } catch (error: any) {
      if (error?.response?.status !== 302) {
        showToast('Failed to download permit', 'error');
      }
    }
  };

  // Format date function
  const formatDate = (dateString?: string) => {
    if (!dateString) return '--';
    return formatDateUtil(dateString, 'M/d/yyyy', dateString);
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '--';
    return formatDateUtil(dateString, 'M/d/yyyy, h:mm:ss a', dateString);
  };

  // Loading state
  if (loading) {
    return (
      <div className="appWrapper">
        <h3 className="pageTitle">
          <Link onClick={() => backToListing()} className="back">
            <img src={Images.BACK} alt="Back" width="22" />
          </Link>
          Activity Details
        </h3>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  // Error state
  if (error || !activity) {
    return (
      <div className="appWrapper">
        <h3 className="pageTitle">
          <Link onClick={() => backToListing()} className="back">
            <img src={Images.BACK} alt="Back" width="22" />
          </Link>
          Activity Details
        </h3>
        <Alert severity="error">
          {error || 'Activity not found'}
        </Alert>
      </div>
    );
  }

  // Calculate checklist progress using template items as source of truth
  const templateChecklistItems = activity.checklist_template_detail?.items || [];
  const completedChecklistCount = templateChecklistItems.filter(templateItem => {
    const existingItem = activity.checklist_items?.find(item =>
      item.checklist_template_item === templateItem.id
    );
    return existingItem?.is_completed || false;
  }).length;
  const totalChecklistCount = templateChecklistItems.length;
  const checklistProgress = totalChecklistCount > 0 ? (completedChecklistCount / totalChecklistCount) * 100 : 0;

  return (
    <div className="appWrapper">
      <h3 className="pageTitle">
        <Link onClick={() => backToListing()} className="back">
          <img src={Images.BACK} alt="Back" width="22" />
        </Link>
        Activity Details
      </h3>

      <div className="appGridContainer">
        <div className="gridWrapper">
          <div className={`${styles.assetDetWrapper}`}>
            <div className={`${styles.assetDetContainer} bordered r10`}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h4>
                  {activity.task_id || activity.taskId}
                </h4>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <span className={`status ${activity.status === 'COMPLETED' ? 'active' : activity.status === 'IN_PROGRESS' ? 'active' : 'inactive'}`}>
                    {activity.status}
                  </span>

                  <PermissionButton 
                    module="permit"
                    permissionAction="view"
                    fallbackPermission="download_at_permit"
                    variant="contained" 
                    color="primary" 
                    onClick={handlePermitDownload}
                    className="button button__solid--primary"
                    sx={{ ml: 'auto' }}
                  >
                    Download Permit
                  </PermissionButton>
                </div>
              </div>

              <section className={`border_bottom ${styles.assetBasic}`}>
                <div className="d_flex">
                  <div className="col">
                    <label className="label">Activity Type</label>
                    <h5 className="body1">
                      {activity.activity_type?.name || '--'}
                    </h5>
                  </div>
                  <div className="col">
                    <label className="label">Location</label>
                    <h5 className="body1">
                      {activity.location?.location_hierarchy || '--'}
                    </h5>
                  </div>
                  <div className="col">
                    <label className="label">Assigned To</label>
                    <h5 className="body1">
                      {activity.assigned_user_name || '--'}
                    </h5>
                  </div>
                  <div className="col">
                    <label className="label">User Group</label>
                    <h5 className="body1">
                      {activity.user_group?.name || '--'}
                    </h5>
                  </div>
                </div>
              </section>

              <section>
                <div className="d_flex wrap">
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Due Date</label>
                    <h5 className="body1">
                      {formatDate(activity.due_date)}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Started</label>
                    <h5 className="body1">
                      {formatDateTime(activity.start_date)}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Completed</label>
                    <h5 className="body1">
                      {formatDateTime(activity.completion_date)}
                    </h5>
                  </div>

                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Schedule</label>
                    <h5 className="body1">
                      {activity.is_one_off ? 'One-Off Activity' : (activity.schedule?.name || activity.schedule_name || '--')}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">SLA Status</label>
                    <h5 className="body1">
                      {activity.slaStatus || '--'}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Description</label>
                    <h5 className="body1">
                      {activity.schedule?.description || '--'}
                    </h5>
                  </div>
                </div>
              </section>

              {/* Asset Information Section */}
              {activity.asset && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Asset Information
                  </h4>
                  <div className="d_flex wrap">
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Asset Name</label>
                      <h5 className="body1">{activity.asset.asset_name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Asset Tag</label>
                      <h5 className="body1">{activity.asset.asset_tag || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Category</label>
                      <h5 className="body1">{activity.asset.asset_category_detail?.name || '--'}</h5>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Status</label>
                      <h5 className="body1">{activity.asset.asset_status_detail?.name || '--'}</h5>
                    </div>
                  </div>
                </section>
              )}

              {/* Movement Tracking Section - the checkout/checkin sub-state
                  Activity's own status field doesn't carry (it stays
                  IN_PROGRESS regardless), derived here from the movement
                  session history instead - mirrors ActivityExecutePage. */}
              {!!activity.movement_tracking_required && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Movement Tracking
                  </h4>
                  <div className="d_flex wrap">
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Check-out</label>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {hasCheckout ? (
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        ) : (
                          <HourglassEmptyIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        )}
                        <Typography variant="body2">{hasCheckout ? 'Recorded' : 'Pending'}</Typography>
                      </Box>
                    </div>
                    <div className="col_33 mt_10 mb_10">
                      <label className="label">Check-in</label>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {hasCheckin ? (
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        ) : (
                          <HourglassEmptyIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        )}
                        <Typography variant="body2">{hasCheckin ? 'Recorded' : 'Pending'}</Typography>
                      </Box>
                    </div>
                  </div>
                  {activity.status === 'IN_PROGRESS' && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      {!hasCheckout && (
                        <Button variant="outlined" color="warning" onClick={() => handleOpenForceDialog('checkout')}>
                          Force Check-out
                        </Button>
                      )}
                      {!hasCheckin && (hasCheckout || activity.is_one_off) && (
                        <Button variant="outlined" color="warning" onClick={() => handleOpenForceDialog('checkin')}>
                          Force Check-in
                        </Button>
                      )}
                    </Box>
                  )}
                </section>
              )}

              {/* Movement Sessions Section - RFID checkin/checkout audit
                  trail, including force-closed steps (RFID enhancement
                  spec's "history must show movement details" requirement) */}
              {!!activity.movement_tracking_required && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Movement Sessions
                  </h4>
                  {loadingMovementSessions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : movementSessionsError ? (
                    <Alert severity="error">{movementSessionsError}</Alert>
                  ) : movementSessions.length === 0 ? (
                    <Alert severity="info">No RFID scans recorded yet for this activity.</Alert>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Event</TableCell>
                            <TableCell>Reader / Location</TableCell>
                            <TableCell>Authorized</TableCell>
                            <TableCell>Notes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {movementSessions.map((session) => (
                            <TableRow key={session.id}>
                              <TableCell>{formatDateTime(session.scanned_at ?? undefined)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={session.movement_status_display || session.movement_status || 'N/A'}
                                  color={
                                    session.movement_status === 'force_checkout' ||
                                    session.movement_status === 'force_checkin'
                                      ? 'warning'
                                      : session.movement_status === 'checkin'
                                        ? 'success'
                                        : 'info'
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{formatReaderLocation(session)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={session.is_authorized ? 'Authorized' : 'Unauthorized'}
                                  color={session.is_authorized ? 'success' : 'error'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {session.force_closed_by_detail
                                  ? `${session.force_closed_by_detail.name}: ${session.force_closure_comments || ''}`
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </section>
              )}

              {/* Cost Information Section */}
              {activity.cost_template_detail?.items && activity.cost_template_detail.items.length > 0 && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Cost Information
                  </h4>
                  <div style={{ marginBottom: '15px' }}>
                    <label className="label">Template: {activity.cost_template_name || 'Cost Template'}</label>
                  </div>

                  {/* Cost Items in bordered container */}
                  <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#fff', padding: '20px' }}>
                    <div className="d_flex wrap">
                      {activity.cost_template_detail.items.map((templateItem, index) => {
                        // Find matching existing cost by template item ID
                        const existingCost = activity.costs?.find(cost =>
                          cost.cost_template_item === templateItem.id
                        );
                        
                        return (
                          <div key={templateItem.id} className="col_33 mt_10 mb_10" style={{ paddingRight: '15px' }}>
                            <label className="label" style={{ marginBottom: '8px', display: 'block', fontWeight: '500' }}>
                              {templateItem.label}
                            </label>
                            <div style={{
                              padding: '12px 14px',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              backgroundColor: '#f8f9fa',
                              fontSize: '16px',
                              fontWeight: '500',
                              color: '#333',
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              ${existingCost?.value ? parseFloat(existingCost.value).toFixed(2) : '0.00'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>
              )}

              {/* Checklist Section */}
              {activity.checklist_template_detail?.items && activity.checklist_template_detail.items.length > 0 && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Checklist
                  </h4>
                  <div style={{ marginBottom: '15px' }}>
                    <label className="label">Template: {activity.checklist_template_name || 'Checklist Template'}</label>
                  </div>
                  
                  {/* Progress Bar */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={checklistProgress}
                        color="success"
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">
                        {`${completedChecklistCount}/${activity.checklist_template_detail.items.length}`}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {`${Math.round(checklistProgress)}% completed`}
                  </Typography>

                  {/* Checklist Items */}
                  <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#fff' }}>
                    <List>
                      {activity.checklist_template_detail.items.map((templateItem, index) => {
                        // Find matching existing checklist item by template item ID
                        const existingItem = activity.checklist_items?.find(item =>
                          item.checklist_template_item === templateItem.id
                        );
                        
                        return [
                          <ListItem
                            key={templateItem.id}
                            sx={{
                              bgcolor: existingItem?.is_completed ? 'rgba(76, 175, 80, 0.08)' : 'transparent',
                              transition: 'background-color 0.3s'
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={existingItem?.is_completed || false}
                                  disabled={true}
                                  color="success"
                                  sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      textDecoration: existingItem?.is_completed ? 'line-through' : 'none',
                                      color: existingItem?.is_completed ? 'text.secondary' : 'text.primary',
                                      fontWeight: existingItem?.is_completed ? 'normal' : 'medium'
                                    }}
                                  >
                                    {templateItem.question}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: existingItem?.is_completed ? 'success.main' : 'warning.main',
                                      display: 'inline-block',
                                      mt: 0.5,
                                      bgcolor: existingItem?.is_completed ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 1
                                    }}
                                  >
                                    {existingItem?.is_completed ? 'Completed' : 'Not completed'}
                                  </Typography>
                                  {existingItem?.is_completed && existingItem.completed_at && (
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                      Completed on: {new Date(existingItem.completed_at).toLocaleString()}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              sx={{ width: '100%' }}
                            />
                          </ListItem>,
                          ...(index < activity.checklist_template_detail.items.length - 1 ? [<Divider key={`divider-${templateItem.id}`} />] : [])
                        ];
                      })}
                    </List>
                  </div>
                </section>
              )}

              {/* Execution Comments Section */}
              {activity.comments && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Execution Comments
                  </h4>
                  <div className="col_100 mt_10 mb_10">
                    <h5 className="body1" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                      {activity.comments}
                    </h5>
                  </div>
                </section>
              )}

              {/* Schedule Files Section */}
              {activity.attachedFiles && activity.attachedFiles.length > 0 && (
                <section className="border_bottom pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Schedule Files
                  </h4>
                  <div className="col_100 mt_10 mb_10">
                    <label className="label">
                      <AttachFileIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom', mr: 0.5 }} />
                      Files attached to the schedule ({activity.attachedFiles.length})
                    </label>
                    <div style={{ marginTop: '10px' }}>
                      {activity.attachedFiles.map((file, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          backgroundColor: '#e3f2fd'
                        }}>
                          <AttachFileIcon sx={{ mr: 1, color: '#1976d2' }} />
                          <div style={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ color: '#1976d2' }}>
                              {file.fileName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {file.fileSize ? `${(file.fileSize / 1024).toFixed(1)} KB` : 'Unknown size'}
                            </Typography>
                          </div>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => handleFileDownload(file)} sx={{ color: '#1976d2' }}>
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* Activity Files Section */}
              {activity.documents_detail && activity.documents_detail.length > 0 && activity.status === 'COMPLETED' && (
                <section className="pb_30 pt_30">
                  <h4 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                    Activity Files
                  </h4>
                  <div className="col_100 mt_10 mb_10">
                    <label className="label">
                      <AttachFileIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom', mr: 0.5 }} />
                      Files uploaded during activity execution ({activity.documents_detail.length})
                    </label>
                    <div style={{ marginTop: '10px' }}>
                      {activity.documents_detail.map((file, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          backgroundColor: '#f9f9f9'
                        }}>
                          <AttachFileIcon sx={{ mr: 1 }} />
                          <div style={{ flexGrow: 1 }}>
                            <Typography variant="body2">
                              {file.filename}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
                            </Typography>
                          </div>
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => handlePreview(file)}>
                              <PreviewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => handleDownload(file)}>
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={forceAction !== null} onClose={handleCloseForceDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {forceAction === 'checkout' ? 'Force Check-out' : 'Force Check-in'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {forceAction === 'checkout'
              ? 'This records a check-out for this activity without an RFID exit scan. Please explain why.'
              : 'This records a check-in for this activity without an RFID entry scan. Please explain why.'}
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Comments"
            value={forceComments}
            onChange={(e) => setForceComments(e.target.value)}
            error={forceComments.trim() === ''}
            helperText={forceComments.trim() === '' ? 'Comments are required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForceDialog} disabled={forceSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleForceSubmit}
            disabled={forceSubmitting || forceComments.trim() === ''}
          >
            {forceSubmitting ? 'Submitting...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ActivityDetailPage;
